import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { OPPORTUNITIES, DETAIL_PHOTOS } from "../data/mockData";
import StaticMap from "../components/StaticMap";
import { SBLogo } from "../components/WebLayout";
import PrototypeBadge from "../components/PrototypeBadge";
import {
  ShieldCheck, MapPin, Users, Clock, CheckCircle2, Lock, Unlock, Phone, Mail,
  Building2, ArrowRight, Circle,
} from "lucide-react";

const CHECKLIST = [
  "Manager Contact Verified",
  "Business Type Confirmed",
  "Traffic Estimated",
  "Photos Reviewed",
  "Duplicate Check Passed",
];

const STEPS = ["Purchased", "Introduction Sent", "Contacted", "Meeting Scheduled", "Won / Lost"];

export default function OpportunityDetail() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const opp = OPPORTUNITIES.find((o) => o.id === id) || OPPORTUNITIES[0];
  const state = sp.get("state") === "unlocked" ? "unlocked" : "locked";
  const setState = (s) => setSp({ state: s });

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#080B0D]/85 border-b border-[#2A2F34]">
        <div className="max-w-[1300px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><SBLogo size={20} /></Link>
          <div className="text-[13px] text-[#A1A1AA]"><Link to="/marketplace" className="hover:text-white">← Back to Marketplace</Link></div>
        </div>
      </header>

      {/* State toggle — PROTOTYPE-ONLY control, remove before production */}
      <div className="max-w-[1300px] mx-auto px-6 pt-6">
        <div className="sb-card p-3 flex items-center gap-3 flex-wrap">
          <PrototypeBadge />
          <span className="sb-eyebrow">View Toggle</span>
          <div className="flex bg-[#0E1215] rounded-lg p-1 border border-[#2A2F34]">
            <button
              onClick={() => setState("locked")}
              data-testid="state-locked"
              className={`px-3 py-1.5 rounded-md text-[12px] flex items-center gap-1.5 ${state === "locked" ? "bg-[#FFC107] text-[#080B0D] font-bold" : "text-[#A1A1AA]"}`}
            >
              <Lock size={12} /> Locked (Before Purchase)
            </button>
            <button
              onClick={() => setState("unlocked")}
              data-testid="state-unlocked"
              className={`px-3 py-1.5 rounded-md text-[12px] flex items-center gap-1.5 ${state === "unlocked" ? "bg-[#FFC107] text-[#080B0D] font-bold" : "text-[#A1A1AA]"}`}
            >
              <Unlock size={12} /> Unlocked (After Purchase)
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-6 py-8 grid lg:grid-cols-[1.5fr_1fr] gap-6">
        {/* Left column */}
        <div className="space-y-5">
          {/* Map / hero */}
          <div className="relative sb-map h-64">
            <div className="absolute inset-0 flex items-center justify-center">
              {state === "locked" ? (
                <div
                  className="rounded-full border-2 border-dashed border-[#FFC107]/60"
                  style={{
                    width: 220, height: 220,
                    background: "radial-gradient(circle, rgba(255,193,7,0.18) 0%, rgba(255,193,7,0.06) 60%, transparent 100%)",
                  }}
                />
              ) : (
                <div className="sb-pin" style={{ position: "static" }} />
              )}
            </div>
            {state === "locked" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-[11px] font-black uppercase tracking-[0.24em] text-[#FFC107]">Approximate</div>
                <div className="text-[15px] font-black tracking-tight text-white mt-1">Opportunity Area</div>
                <div className="text-[10.5px] text-[#A1A1AA] mt-1">Exact address unlocks after purchase</div>
              </div>
            )}
            <div className="absolute bottom-3 left-3 sb-pill sb-pill-gray">
              {state === "locked" ? "Radius shown is illustrative" : "Exact location revealed"}
            </div>
          </div>

          {state === "unlocked" && (
            <div className="grid grid-cols-3 gap-2">
              {(DETAIL_PHOTOS[opp.category] || DETAIL_PHOTOS.warehouse).slice(0, 3).map((src, i) => {
                const labels = ["Exterior", "Interior", "Proposed vending area"];
                return (
                  <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden sb-image-dim relative">
                    <img
                      src={src}
                      alt={`${opp.category} — ${labels[i]}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const el = e.currentTarget;
                        el.style.display = "none";
                        const p = el.parentElement;
                        if (p && !p.querySelector('[data-fallback]')) {
                          const div = document.createElement("div");
                          div.setAttribute("data-fallback", "true");
                          div.className = "absolute inset-0 flex flex-col items-center justify-center bg-[#0E1215] border border-[#2A2F34] text-[10px] uppercase tracking-widest text-[#52525b]";
                          div.innerHTML = '<div>Photo</div><div style="opacity:.6;margin-top:4px">Unavailable</div>';
                          p.appendChild(div);
                        }
                      }}
                    />
                    <div className="absolute bottom-1.5 left-1.5 text-[9px] uppercase tracking-widest text-white/70 font-semibold" style={{ textShadow: "0 1px 2px rgba(0,0,0,.8)" }}>
                      {labels[i]}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Business info block */}
          <div className="sb-card p-6">
            {state === "unlocked" ? (
              <>
                <div className="sb-eyebrow">Business</div>
                <div className="mt-2 text-[26px] font-black">{opp.business}</div>
                <div className="mt-1 text-[14px] text-[#A1A1AA] flex items-center gap-2">
                  <MapPin size={14} className="text-[#FFC107]" />
                  {opp.address}
                </div>
              </>
            ) : (
              <>
                <div className="sb-eyebrow">Approximate Area</div>
                <div className="mt-2 text-[26px] font-black">{opp.city}, {opp.state} <span className="text-[#A1A1AA] font-normal">{opp.zip}</span></div>
                <div className="mt-1 text-[13px] text-[#A1A1AA] flex items-center gap-1.5"><Lock size={12} /> Exact business name & address revealed after purchase.</div>
              </>
            )}

            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-[13px]">
              <div className="flex items-start gap-2"><Building2 size={16} className="text-[#FFC107] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Location Type</div><div className="capitalize">{opp.category}</div></div></div>
              <div className="flex items-start gap-2"><Users size={16} className="text-[#FFC107] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Traffic</div><div>~{opp.employees} employees</div></div></div>
              <div className="flex items-start gap-2"><Clock size={16} className="text-[#FFC107] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Hours</div><div>{opp.open247 ? "Open 24/7" : "Business hours"}</div></div></div>
              <div className="flex items-start gap-2"><ShieldCheck size={16} className="text-[#FFC107] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Shifts</div><div>{opp.shifts > 0 ? `${opp.shifts} shifts` : "Continuous"}</div></div></div>
              <div className="flex items-start gap-2 sm:col-span-2"><CheckCircle2 size={16} className="text-[#22C55E] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Current Vending</div><div>{opp.currentVending}</div></div></div>
              <div className="flex items-start gap-2 sm:col-span-2"><CheckCircle2 size={16} className="text-[#22C55E] mt-0.5" /><div><div className="sb-eyebrow" style={{ fontSize: 10 }}>Requested Machine Type</div><div>{opp.machines}</div></div></div>
            </div>
          </div>

          {/* Verification checklist */}
          <div className="sb-card p-6">
            <div className="sb-eyebrow">Verification Checklist</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {CHECKLIST.map((c) => (
                <div key={c} className="flex items-center gap-2 text-[13px]">
                  <CheckCircle2 size={16} className="text-[#22C55E]" /> {c}
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-[#2A2F34] flex items-center justify-between text-[13px]">
              <span className="text-[#A1A1AA]">Listing Freshness</span>
              <span>Listed {opp.listedDaysAgo} day{opp.listedDaysAgo !== 1 && "s"} ago</span>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] text-[#A1A1AA] mb-1">
                <span>Verification Level</span>
                <span className="text-white font-bold">{opp.verification}/100</span>
              </div>
              <div className="sb-progress-track"><div className="sb-progress-fill" style={{ width: `${opp.verification}%` }} /></div>
            </div>
          </div>

          {/* Unlocked-only sections */}
          {state === "unlocked" && (
            <>
              <div className="sb-card p-6">
                <div className="sb-eyebrow">Authorized Contact</div>
                <div className="mt-3 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FFC107]/10 text-[#FFC107] flex items-center justify-center font-black">
                    {opp.manager.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <div className="font-black text-[16px]">{opp.manager}</div>
                    <div className="text-[12px] text-[#A1A1AA]">{opp.title}</div>
                  </div>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[13px]">
                  <div className="sb-card p-3"><div className="sb-eyebrow" style={{ fontSize: 10 }}>Phone</div><div className="mt-1 flex items-center gap-1.5"><Phone size={12} /> {opp.phone}</div></div>
                  <div className="sb-card p-3"><div className="sb-eyebrow" style={{ fontSize: 10 }}>Email</div><div className="mt-1 flex items-center gap-1.5 truncate"><Mail size={12} /> {opp.email}</div></div>
                  <div className="sb-card p-3 sm:col-span-2"><div className="sb-eyebrow" style={{ fontSize: 10 }}>Best Contact Time</div><div className="mt-1">{opp.bestContact}</div></div>
                </div>
                <button data-testid="contact-location" className="mt-5 sb-btn-primary w-full">
                  Contact Location <ArrowRight size={16} />
                </button>
              </div>

              <div className="sb-card p-6">
                <div className="sb-eyebrow">Status Tracker</div>
                <div className="mt-5 flex items-center justify-between gap-2">
                  {STEPS.map((s, i) => {
                    const done = i < 2;
                    const current = i === 1;
                    return (
                      <React.Fragment key={s}>
                        <div className="flex flex-col items-center gap-1.5 text-center w-24">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done || current ? "bg-[#FFC107] text-[#080B0D]" : "bg-[#15191D] border border-[#2A2F34] text-[#52525b]"}`}>
                            {done && !current ? <CheckCircle2 size={16} /> : <Circle size={14} />}
                          </div>
                          <div className={`text-[10px] font-bold uppercase tracking-wider ${done || current ? "text-white" : "text-[#52525b]"}`}>{s}</div>
                        </div>
                        {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < 1 ? "bg-[#FFC107]" : "bg-[#2A2F34]"}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right column */}
        <aside className="lg:sticky lg:top-24 h-max space-y-4">
          <div className="sb-card p-6">
            <div className="sb-pill sb-pill-gold">Exclusive Opportunity</div>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-[#A1A1AA]">Price</div>
            <div className="text-[42px] font-black leading-none">${opp.price}</div>
            {state === "locked" ? (
              <button
                data-testid="buy-unlock"
                onClick={() => setState("unlocked")}
                className="mt-6 sb-btn-primary w-full"
              >
                Buy & Unlock <Unlock size={16} />
              </button>
            ) : (
              <div className="mt-6 sb-pill sb-pill-green w-full justify-center py-2">
                <CheckCircle2 size={12} /> Purchased
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-[#2A2F34]">
              <div className="flex items-start gap-2 text-[12.5px] text-[#A1A1AA]">
                <ShieldCheck size={16} className="text-[#FFC107] mt-0.5" />
                <div>
                  <div className="text-white font-bold uppercase tracking-wider text-[11px]">7-Day Verification Protection</div>
                  <p className="mt-1.5 leading-relaxed">
                    SpotBounty protection may apply if material information in the listing is inaccurate, the verified
                    business contact information is invalid, or the business denies having verified its interest through
                    SpotBounty.
                  </p>
                  <p className="mt-2 text-[11px] text-[#52525b]">
                    Final eligibility is subject to the SpotBounty Verification Protection Policy.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="sb-card p-4 flex items-start gap-2 text-[12px] text-[#A1A1AA]">
            <ShieldCheck size={14} className="text-[#FFC107] mt-0.5 shrink-0" />
            SpotBounty verifies the information shown in this listing but does not guarantee vending revenue, placement success or profitability.
          </div>
        </aside>
      </div>
    </div>
  );
}
